/**
 * The single Koda context builder for the counseling app.
 *
 * Koda only needs the student the counselor has open and the ids on the page; it reads
 * everything about them back through its own tools. So this sends ids, never the text
 * already on screen. The one exception is a counseling session that has not been saved
 * yet: it has no id, so its title and summary travel instead.
 *
 * The subject is not sticky here. The backend remembers who the thread is about, so a page
 * only ever states what it currently has open.
 */

const ENTITY_KEYS = [
	'shortlistId',
	'universityId',
	'courseId',
	'essayId',
	'applicationTargetId',
	'promptId',
	'sessionNoteId',
]

const PAGE_KEYS = ['source', 'activeTab']

const trimmed = value => String(value ?? '').trim()

const pick = (source, keys) => {
	const out = {}
	keys.forEach(key => {
		const value = trimmed(source?.[key])
		if (value) out[key] = value
	})
	return out
}

/**
 * @param {object} input
 * @param {{studentId?: string, studentName?: string}} [input.subject] Who the page is about.
 * @param {object} [input.entities] Ids the page has open, keyed as in ENTITY_KEYS.
 * @param {{title?: string, summary?: string}} [input.session] An unsaved session note only.
 * @returns {object} The kodaContext envelope, or {} when the page has nothing to say.
 */
export function buildKodaContext({ source, activeTab, subject, entities, session } = {}) {
	const context = pick({ source, activeTab }, PAGE_KEYS)

	const resolvedSubject = pick(subject, ['studentId', 'studentName'])
	if (Object.keys(resolvedSubject).length) context.subject = resolvedSubject

	const resolvedEntities = pick(entities, ENTITY_KEYS)
	if (Object.keys(resolvedEntities).length) context.entities = resolvedEntities

	// A saved note is read from the database, and sending both is a contract violation.
	if (!resolvedEntities.sessionNoteId) {
		const resolvedSession = pick(session, ['title', 'summary'])
		if (Object.keys(resolvedSession).length) context.session = resolvedSession
	}

	return context
}

/** Pages whose ids are in the url; nothing has to be wired up for these. */
const ROUTES = [
	{
		pattern: /^\/student_profile\/([^/]+)/,
		build: ([studentId]) => ({
			source: 'student_profile',
			subject: { studentId: decodeURIComponent(studentId) },
		}),
	},
	{
		pattern: /^\/applications\/([^/]+)/,
		build: ([applicationTargetId]) => ({
			source: 'application_details',
			entities: { applicationTargetId },
		}),
	},
]

export function kodaContextFromPath(pathname) {
	const path = pathname || ''
	const route = ROUTES.find(r => r.pattern.test(path))
	if (!route) return {}
	const [, ...groups] = path.match(route.pattern)
	return buildKodaContext(route.build(groups))
}

/**
 * Stamp a page's context with the path that produced it. The chat panel outlives the page,
 * so without this a student from a page the counselor already left would keep travelling.
 */
export const stampKodaContext = context =>
	context ? { pathname: window.location.pathname, context } : null

const mergeLayer = (base, layer) => {
	if (!layer || typeof layer !== 'object') return base || {}
	const next = { ...(base || {}), ...layer }
	if (base?.subject || layer.subject) {
		next.subject = { ...(base?.subject || {}), ...(layer.subject || {}) }
		if (!Object.keys(next.subject).length) delete next.subject
	}
	if (base?.entities || layer.entities) {
		next.entities = { ...(base?.entities || {}), ...(layer.entities || {}) }
		if (!Object.keys(next.entities).length) delete next.entities
	}
	if (layer.session) next.session = layer.session
	// Saved note id wins over unsaved session text.
	if (next.entities?.sessionNoteId) delete next.session
	return next
}

/**
 * What to send with this message: the route says which page and student are open, and a page
 * adds only what the url cannot say (a student the url identifies by application, say).
 * Overrides deep-merge subject/entities so Ask Koda cannot wipe an open applicationTargetId.
 */
export function kodaContextForSend({ pathname, pageContext, override } = {}) {
	const stamped = pageContext?.pathname === pathname ? pageContext.context : null
	const merged = mergeLayer(mergeLayer(kodaContextFromPath(pathname), stamped), override)
	return Object.keys(merged).length ? merged : undefined
}
